"use client";
import React, {useState, useEffect, Ref} from 'react';
import EditTemplateRenderer from './EditTemplateRenderer.tsx';
import {Benefit, JobTemplate, ContentData, Block, MediaElement} from '../interfaces';
import MediaBlockSettingsDialog from '../dialogs/content/MediaBlockSettingsDialog.tsx';
import {useServerProvider} from "../use-server-provider.tsx";
import {LocaleProvider} from "../context/localeContext.tsx";
import ShadowDomWrapper from "./ShadowDomWrapper.tsx";
import {filterReferencedFilesWithContent} from '../utils.ts';
import {applyTranslationOverrides, TranslationOverrides} from '../i18n/config';

interface TemplateContentEditorProps {
  ref: Ref<HTMLInputElement>;
  template: JobTemplate;
  contentData: ContentData<unknown>;
  locale: string;
  fallbackLocale: string;
  availableLocales: string[];
  benefits?: Benefit[];
  onChange?: (value: ContentData<unknown>) => void;
  onFilesChange?: (files: MediaElement[]) => void;
  newMediaCallback?: (args: { blockId: string }) => Promise<string>;
  mediaLibraryCallback?: (args: { blockId: string }) => Promise<string>;
  dragAndDropMediaCallback?: (args: { blockId: string; file: File }) => Promise<string>;
  files?: MediaElement[];
  namespace?: string;
  translationOverrides?: TranslationOverrides;
  primaryColor?: string;
  useIframe?: boolean;
}

const TemplateContentEditor: React.FC<TemplateContentEditorProps> = ({ fallbackLocale, locale, availableLocales, template, contentData: originalContentData, benefits, onChange, onFilesChange, newMediaCallback, mediaLibraryCallback, dragAndDropMediaCallback, files, namespace = 'template-content-editor', translationOverrides, primaryColor = '#3b82f6', useIframe = true }) => {
  const [settingsBlock, setSettingsBlock] = useState<Block | null>(null);
  const [contentData, setContentData] = useState<ContentData<unknown>>(originalContentData);

  const handleSetContentData = (newContentData: ContentData<unknown>) => {
    setContentData(newContentData);
    if (onChange) {
      onChange(newContentData);
    }
  };

  // On first init, emit all default content from template blocks to ensure data is set at the start
  useEffect(() => {
    const mergedContentData = { ...contentData };
    if (!mergedContentData[locale]) {
      mergedContentData[locale] = {};
    }

    let hasDefaults = false;
    for (const block of template.blocks) {
      if (block.defaultContent?.[locale]) {
        if (!mergedContentData[locale][block.id]) {
          mergedContentData[locale][block.id] = {};
        }
        const defaults = block.defaultContent[locale] as Record<string, unknown>;
        for (const [field, value] of Object.entries(defaults)) {
          if ((mergedContentData[locale][block.id] as Record<string, unknown>)[field] === undefined) {
            (mergedContentData[locale][block.id] as Record<string, unknown>)[field] = value;
            hasDefaults = true;
          }
        }
      }
    }

    if (hasDefaults) {
      handleSetContentData(mergedContentData);
    }
  }, []); // Empty dependency array - only run on mount

  // Apply translation overrides when they change
  useEffect(() => {
    applyTranslationOverrides(translationOverrides);
  }, [translationOverrides]);

  useEffect(() => {
    // Notify about file changes whenever content or template changes
    if (onFilesChange) {
      const referencedFiles = filterReferencedFilesWithContent(template.blocks, contentData, files);
      if (JSON.stringify(referencedFiles) !== JSON.stringify(files)) {
        onFilesChange(referencedFiles);
      }
    }
  }, [onFilesChange, template.blocks, contentData, files]);

  const handleContentChange = (blockId: string, field: string, value: string) => {
    const newContentData = { ...contentData };
    if (!newContentData[locale]) {
      newContentData[locale] = {};
    }
    if (!newContentData[locale][blockId]) {
      newContentData[locale][blockId] = {};
    }
    (newContentData[locale][blockId] as Record<string, unknown>)[field] = value;
    handleSetContentData(newContentData);
  };
  useServerProvider<(blockId: string, field: string, value: any) => void>('onContentChange', handleContentChange)

  const handleBlockSettingsClick = (block: any) => {
    // Only allow settings for media blocks
    if (block.type === 'mediaBlock') {
      setSettingsBlock(block);
    }
  };
  
  const handleCloseSettingsDialog = () => {
    setSettingsBlock(null);
  };
  
  const handleSaveMediaSettings = (updatedContentData: any) => {
    handleSetContentData(updatedContentData);
    setSettingsBlock(null);
  };

  return (
    <>
      <EditTemplateRenderer
        template={template}
        contentData={contentData}
        locale={locale}
        fallbackLocale={fallbackLocale}
        editable={true}
        onBlockSettingsClick={handleBlockSettingsClick}
        benefits={benefits}
        newMediaCallback={newMediaCallback}
        mediaLibraryCallback={mediaLibraryCallback}
        dragAndDropMediaCallback={dragAndDropMediaCallback}
        setContentData={handleSetContentData}
        files={files}
        namespace={namespace}
        primaryColor={primaryColor}
        useIframe={useIframe}
        availableLocales={availableLocales}
      />
      <LocaleProvider
        availableLocales={availableLocales}
        fallbackLocale={fallbackLocale}
      >
        <ShadowDomWrapper>
          {settingsBlock && (
            <MediaBlockSettingsDialog
              block={settingsBlock}
              contentData={contentData}
              onClose={handleCloseSettingsDialog}
              onSave={handleSaveMediaSettings}
              availableLocales={[locale]}
              newMediaCallback={newMediaCallback}
              mediaLibraryCallback={mediaLibraryCallback}
              files={files}
            />
          )}
        </ShadowDomWrapper>
      </LocaleProvider>
    </>
  );
};

export default TemplateContentEditor;
